/**
 * Per-route <head> management without a helmet dependency.
 * - title ≤ 60 chars, description ≤ 155 (enforced here so no route can regress)
 * - canonical, og:url, og:title, og:description, twitter:* follow the current route
 * - robots noindex where asked; 404 pages also emit prerender-status-code 404 so the crawler
 *   prerender layer can return a real 404 (prerender.io / rendertron convention)
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE = "https://sunbizcon.com";
const DEFAULT_TITLE = "Solar Project Management Software for Indian EPCs | SunBiz";
const DEFAULT_DESC = "Bill audit, policy-correct sizing, proposals, bank DPRs, 8-phase project plans, GST POs/PIs and a client portal for Indian solar EPCs. 14-day free trial.";

/** Public routes and their head data. Everything under /app, /superadmin and the client portal is noindex. */
export const ROUTE_META = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESC, priority: 1.0, changefreq: "weekly" },
  "/solar-project-management-software-india": {
    title: "Solar Project Management Software in India | SunBiz",
    description: "Solar project management software built for Indian EPCs. 63-87 auto-generated tasks, DISCOM and CEIG approvals, GST-ready POs. From ₹2,999/mo. Free trial.",
    priority: 0.9, changefreq: "weekly",
  },
  "/OA-Assessment-MH": {
    title: "Maharashtra Open Access Solar Assessment | SunBiz",
    description: "Check captive and group-captive open access solar for a Maharashtra HT consumer — MSEDCL ToD, banking, landed tariff and plant size from the bill.",
    priority: 0.6, changefreq: "monthly",
  },
  "/tools/ppa-offer-generator": { title: "PPA offer generator | SunBiz", description: "Internal PPA offer generator.", robots: "noindex, nofollow" },
  "/login": { title: "Log in | SunBiz", description: "Log in to your SunBiz workspace.", robots: "noindex, follow" },
  "/register": { title: "Start your free 14-day trial | SunBiz", description: "Create a SunBiz workspace for your solar EPC. No credit card required.", robots: "noindex, follow" },
};

/** Routes listed in sitemap.xml — the indexable subset of ROUTE_META, in one place for the build-time generator. */
export const SITEMAP_ROUTES = Object.entries(ROUTE_META).filter(([, m]) => !m.robots).map(([path, m]) => ({ path, priority: m.priority, changefreq: m.changefreq }));

const clamp = (s, n) => (s && s.length > n ? s.slice(0, n - 1).replace(/\s+\S*$/, "") + "…" : s);

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

/** Apply head tags for the current route. `override` lets a page supply its own (title, description, robots, status, jsonLd). */
export function applySeo(pathname, override = {}) {
  const base = ROUTE_META[pathname] || {};
  const isPrivate = /^\/(app|superadmin|client\/portal|onboarding)(\/|$)/.test(pathname);
  const status = override.status || (ROUTE_META[pathname] || isPrivate ? 200 : 404);
  const title = clamp(override.title || base.title || (status === 404 ? "Page not found | SunBiz" : DEFAULT_TITLE), 60);
  const description = clamp(override.description || base.description || DEFAULT_DESC, 155);
  const robots = override.robots || base.robots || (isPrivate || status === 404 ? "noindex, nofollow" : null);
  const url = SITE + (pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));
  document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "robots", robots);
  setMeta("name", "prerender-status-code", status === 404 ? "404" : null);
  setLink("canonical", url);
  setMeta("property", "og:url", url);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setJsonLd("seo-jsonld", override.jsonLd || null);
}

/** Mount once inside the router: keeps the head in sync on every navigation. Pages may call useSeo() to override. */
export function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => { applySeo(pathname); }, [pathname]);
  return null;
}

/** Page-level override (runs after RouteSeo because children effects run after the parent's). */
export function useSeo(override) {
  const { pathname } = useLocation();
  useEffect(() => { applySeo(pathname, override); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}
